import { Link } from "react-router-dom";
import { Activity, ArrowUpRight, Mail, MessageCircle, Phone } from "lucide-react";
import {
  getWhatsAppLink,
  NAVICARGO_EMAIL,
  NAVICARGO_EMAIL_LINK,
  NAVICARGO_PHONE,
  NAVICARGO_PHONE_LINK,
} from "@/lib/contact";
import { useLanguage } from "@/lib/i18n";

const Footer = () => {
  const { isEnglish, t } = useLanguage();
  const quickLinks = [
    { name: "Nosotros", nameEn: "About us", path: "/about" },
    { name: "Servicios", nameEn: "Services", path: "/services" },
    { name: "Contacto", nameEn: "Contact", path: "/contact" },
  ];

  const services = [
    { name: "Inspección de origen", nameEn: "Origin inspection", slug: "inspeccion-origen" },
    { name: "Búsqueda de productos", nameEn: "Product sourcing", slug: "busqueda-productos" },
    { name: "Transporte internacional", nameEn: "International transportation", slug: "transporte-internacional" },
    { name: "Verificación de empresas", nameEn: "Company verification", slug: "verificacion-empresas" },
    { name: "Cierre de negociaciones", nameEn: "Negotiation closing", slug: "cierre-negociaciones" },
    { name: "Despacho aduanero", nameEn: "Customs clearance", slug: "despacho-aduanero" },
    { name: "Importación y exportación", nameEn: "Import and export", slug: "importacion-exportacion" },
  ];

  return (
    <footer data-testid="main-footer" className="bg-[#08090A] border-t border-zinc-800/70">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-x-10 gap-y-12 lg:gap-x-14">
          <div className="md:col-span-5 lg:col-span-4">
            <Link to="/" className="inline-flex items-center mb-7" aria-label="Navi Cargo, inicio">
              <img
                src="/navicargo-logo-white-transparent.png"
                alt="Navi Cargo"
                className="block w-[132px] h-auto object-contain"
              />
            </Link>
            <p className="max-w-sm text-zinc-400 text-[13px] leading-[1.75] mb-6">
              {t("footer.tagline", "Tu carga avanza cuando cada decisión tiene respaldo. Conectamos proveedores, productos y mercados para mover tu negocio con claridad.")}
            </p>
            <a
              href={getWhatsAppLink("Hola NaviCargo, quiero hablar sobre una operación logística.")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#22C55E] hover:text-[#4ADE80] text-[13px] font-semibold transition-colors group"
            >
              {t("footer.cta", "Cuéntanos qué quieres mover")}
              <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </div>

          <div className="md:col-span-3 lg:col-span-2">
            <h4 className="text-zinc-500 font-semibold mb-5 text-[10px] uppercase tracking-[0.2em]">{t("footer.explore", "Explora")}</h4>
            <ul className="space-y-3.5">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-zinc-400 hover:text-[#22C55E] transition-colors text-[13px] flex items-center gap-2 group"
                  >
                    {isEnglish ? link.nameEn : link.name}
                    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href="https://nnexio.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-400 hover:text-[#22C55E] transition-colors text-[13px]"
                >
                  {t("nav.clients", "Clientes")}
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4 lg:col-span-3">
            <h4 className="text-zinc-500 font-semibold mb-5 text-[10px] uppercase tracking-[0.2em]">{t("footer.solutions", "Soluciones")}</h4>
            <ul className="grid grid-cols-1 gap-3.5">
              {services.map((service) => (
                <li key={service.slug}>
                  <Link
                    to={`/services/${service.slug}`}
                    className="text-zinc-400 hover:text-[#22C55E] transition-colors text-[13px]"
                  >
                    {isEnglish ? service.nameEn : service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-5 lg:col-span-3">
            <h4 className="text-zinc-500 font-semibold mb-5 text-[10px] uppercase tracking-[0.2em]">{t("footer.letsTalk", "Hablemos")}</h4>
            <p className="text-zinc-500 text-[12px] leading-relaxed max-w-xs mb-5">
              {t("footer.direct", "Atención directa para planear tu próxima operación logística.")}
            </p>
            <ul className="space-y-4">
              <li>
                <a href={NAVICARGO_PHONE_LINK} className="flex items-center gap-3 text-zinc-400 hover:text-white transition-colors">
                  <Phone className="w-3.5 h-3.5 text-[#22C55E] flex-shrink-0" />
                  <span className="text-[13px]">{NAVICARGO_PHONE}</span>
                </a>
              </li>
              <li>
                <a href={NAVICARGO_EMAIL_LINK} className="flex items-center gap-3 text-zinc-400 hover:text-white transition-colors">
                  <Mail className="w-3.5 h-3.5 text-[#22C55E] flex-shrink-0" />
                  <span className="text-[13px] break-all">{NAVICARGO_EMAIL}</span>
                </a>
              </li>
              <li>
                <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-zinc-400 hover:text-white transition-colors">
                  <MessageCircle className="w-3.5 h-3.5 text-[#22C55E] flex-shrink-0" />
                  <span className="text-[13px]">{t("footer.quote", "Cotizar por WhatsApp")}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-zinc-800/70 mt-14 pt-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <p className="text-zinc-600 text-[11px]">© {new Date().getFullYear()} Navicargo Logistics. {t("footer.rights", "Todos los derechos reservados.")}</p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px]">
            <span className="inline-flex items-center gap-2 text-zinc-600">
              <Activity className="w-3.5 h-3.5 text-[#22C55E]" />
              {t("footer.monitored", "Sitio monitoreado por")}
            </span>
            <a
              href="https://nnexio.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visitar nnexio.com"
              className="inline-flex items-center gap-1.5 text-zinc-300 hover:text-[#22C55E] transition-colors font-semibold"
            >
              Nnexio
              <ArrowUpRight className="w-3 h-3" />
            </a>
            <span className="hidden sm:inline-block w-px h-3 bg-zinc-800" />
            <span className="text-zinc-600">{t("footer.global", "Logística global · MX")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;